import * as React from 'react'
import { cn } from '../../lib/cn'
import { PipelineConnector, type PipelineConnectorProps } from './PipelineConnector'
import { PipelineTerminal } from './PipelineTerminal'
import { StageColumn } from './StageColumn'
import { SystemChip, type SystemChipProps } from './SystemChip'
import type { PipelineStageData, PipelineTerminalData } from './types'

/* -------------------------------------------------------------------------- */
/* Slot kinds + helpers                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Slot kinds. Each kind controls how the surrounding wrapper sizes
 * itself; the slot node renders inside that wrapper as-is.
 *
 * - `stage` — 320px (one of the 4 main hook columns)
 * - `sys` — 230px (system chip between stages)
 * - `branch` — fluid (the parallel-lane row + spine + fork connectors)
 * - `terminal` — 96px (round disk on either end)
 * - `connector` — 32px (gap with a horizontal line)
 * - `forkGap` — 56px (extra-wide gap for fork connectors)
 * - `custom` — caller-controlled width (set on the node itself)
 */
export type CanvasSlotKind =
  | 'stage'
  | 'sys'
  | 'branch'
  | 'terminal'
  | 'connector'
  | 'forkGap'
  | 'custom'

export interface CanvasSlot {
  id: string
  kind: CanvasSlotKind
  node: React.ReactNode
}

/** Build a stage slot from a `PipelineStageData`. */
export function stageSlot(stage: PipelineStageData): CanvasSlot {
  return {
    id: `stage-${stage.id}`,
    kind: 'stage',
    node: <StageColumn stage={stage} />,
  }
}

/**
 * Build a system-chip slot (the wide chip that sits between stages,
 * e.g. `identity_lookup` / `issue_session`).
 */
export function sysChipSlot(id: string, props: SystemChipProps): CanvasSlot {
  return {
    id: `sys-${id}`,
    kind: 'sys',
    node: <SystemChip {...props} />,
  }
}

/** Build a terminal slot (Intake / Allow / Deny). */
export function terminalSlot(terminal: PipelineTerminalData): CanvasSlot {
  return {
    id: `term-${terminal.id}`,
    kind: 'terminal',
    node: <PipelineTerminal terminal={terminal} />,
  }
}

/**
 * Build a clickable terminal slot. `onClick` is threaded from the caller
 * into `PipelineTerminalData.onClick`, which causes `PipelineTerminal`
 * to render as a `<button>` with an `aria-label` derived from the label.
 */
export function clickableTerminalSlot(
  terminal: PipelineTerminalData,
  onClick: () => void,
): CanvasSlot {
  return terminalSlot({ ...terminal, onClick })
}

/** Build a plain connector slot between two content slots. */
export function connectorSlot(id: string, props?: PipelineConnectorProps): CanvasSlot {
  return {
    id: `conn-${id}`,
    kind: 'connector',
    node: <PipelineConnector {...props} />,
  }
}

/* -------------------------------------------------------------------------- */
/* PipelineCanvas                                                             */
/* -------------------------------------------------------------------------- */

export interface PipelineCanvasProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Slots are rendered left-to-right in order. The caller is
   * responsible for inserting `connector` slots between content slots;
   * this keeps the shell layout-agnostic and lets consumers swap
   * connector styles (plain line, fork, none) without prop explosion.
   */
  slots: CanvasSlot[]
  /**
   * Optional fixed height. Defaults to filling the parent. Pass a CSS
   * length (e.g. `'72vh'` or `600`) when the canvas is embedded in a
   * layout that doesn't already constrain its height.
   */
  height?: string | number
  /** Min row height — defaults to 600px. */
  minHeight?: string | number
  /** Content rendered above the canvas (e.g. a top bar + breadcrumbs). */
  topSlot?: React.ReactNode
  /** Content rendered below the canvas (e.g. a hint strip / legend). */
  bottomSlot?: React.ReactNode
}

/**
 * Horizontal scrollable canvas shell for the unified auth-pipeline
 * DAG editor.
 *
 * The component is composition-driven: callers supply an ordered list
 * of slots. The shell handles widths, the dot-grid canvas
 * background, and horizontal scroll affordance. The four-stage
 * structure (Pre-Auth -> identity_lookup -> Authenticate (branched) ->
 * Post-Auth -> issue_session -> Post-Issue) is assembled by the
 * caller using the `stageSlot`, `sysChipSlot`, `terminalSlot`, and
 * `connectorSlot` helpers.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.canvas`.
 */
export const PipelineCanvas = React.forwardRef<HTMLDivElement, PipelineCanvasProps>(
  function PipelineCanvas(
    { className, slots, height, minHeight = 600, topSlot, bottomSlot, style, ...rest },
    ref,
  ) {
    const containerStyle: React.CSSProperties = {
      background: 'var(--pe-bg-canvas)',
      color: 'var(--pe-text-primary)',
      backgroundImage:
        'radial-gradient(circle at 1px 1px, color-mix(in srgb, var(--pe-text-primary) 6%, transparent) 1px, transparent 0)',
      backgroundSize: '28px 28px',
      ...(height !== undefined ? { height } : {}),
      ...style,
    }

    return (
      <div
        ref={ref}
        className={cn('flex flex-col w-full h-full', className)}
        style={containerStyle}
        data-testid="pipeline-canvas"
        {...rest}
      >
        {topSlot}
        {/* Scrollable region: needs role + tabIndex + accessible name
            so axe `scrollable-region-focusable` passes — keyboard
            users must be able to focus the canvas and pan with arrow keys. */}
        <div
          className="flex-1 overflow-x-auto overflow-y-hidden px-7 py-6"
          style={{ minHeight }}
          data-testid="pipeline-canvas-scroller"
          role="region"
          aria-label="Pipeline canvas"
          tabIndex={0}
        >
          <div className="flex h-full items-stretch w-fit" data-testid="pipeline-canvas-row">
            {slots.map((slot) => (
              <div
                key={slot.id}
                className={cn(
                  'flex h-full items-stretch',
                  slot.kind === 'sys' || slot.kind === 'terminal'
                    ? 'items-center justify-center'
                    : undefined,
                )}
                style={slotWrapperStyle(slot.kind)}
                data-slot-id={slot.id}
                data-slot-kind={slot.kind}
              >
                {slot.node}
              </div>
            ))}
          </div>
        </div>
        {bottomSlot}
      </div>
    )
  },
)

function slotWrapperStyle(kind: CanvasSlotKind): React.CSSProperties {
  switch (kind) {
    case 'stage':
      return { width: 320, minWidth: 320 }
    case 'sys':
      return { width: 230, minWidth: 230 }
    case 'terminal':
      return { width: 96, minWidth: 96 }
    case 'connector':
      return { width: 32, minWidth: 32 }
    case 'forkGap':
      return { width: 56, minWidth: 56 }
    default:
      return {}
  }
}
